import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { LoginStore } from "../../../stores/login.store";
import { ElectionApi } from "./election.api";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import type { DropDownItem } from "../../../pojo/DropDownItem";
import { StateService } from "../../../services/state.service";
import type { S25States } from "../../../services/state/state.map";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { S25RichTextEditorModule } from "../../s25-rich-text-editor/s25.rich.text.editor.module";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";
import { ElectionAdminReturnStore } from "./election.admin.return.store";

@TypeManagerDecorator("s25-ng-election-manual-candidate-page")
@Component({
    selector: "s25-ng-election-manual-candidate-page",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        S25ButtonComponent,
        S25DropdownPaginatedModule,
        S25RichTextEditorModule,
        S25FilestoreImageComponent,
    ],
    host: { class: "election-manual-candidate-page" },
    template: `
        <div class="c-padding--double">
            <p class="c-margin-bottom--single">
                <s25-ng-button [type]="'outline'" [onClick]="backToList">← Back to list</s25-ng-button>
            </p>
            <h1>Add Election Candidate</h1>
            @if (!login.loggedIn()) {
                <p>Sign in.</p>
            } @else {
                <form class="election-manual-candidate__form" (ngSubmit)="$event.preventDefault()">
                    <div class="form-group c-margin-bottom--single">
                        <label>Election</label>
                        <div class="election-manual-candidate__control">
                            <s25-ng-dropdown-search-criteria
                                class="c-input"
                                [type]="'elections'"
                                [value]="selectedElectionDropDown()"
                                (valueChange)="onElectionDropDownChange($event)"
                            />
                        </div>
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>Position</label>
                        @if (electionId() == null) {
                            <p class="c-text--secondary">Select an election first.</p>
                        } @else {
                            @for (electionIdRemount of positionCriteriaRemountByElectionId(); track electionIdRemount) {
                                <div class="election-manual-candidate__control">
                                    <s25-ng-dropdown-search-criteria
                                        class="c-input"
                                        [type]="'electionPositionsForElection'"
                                        [customFilterValue]="electionIdRemount.toString()"
                                        [value]="selectedPositionDropDown()"
                                        (valueChange)="onPositionDropDownChange($event)"
                                    />
                                </div>
                            }
                        }
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>Contact</label>
                        <div class="election-manual-candidate__control">
                            <s25-ng-dropdown-search-criteria
                                class="c-input"
                                [type]="'contacts'"
                                [value]="contactItem()"
                                (valueChange)="onContactItemChange($event)"
                                [placeholder]="'Search contacts'"
                            />
                        </div>
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>Campaign brief</label>
                        <s25-ng-rich-text-editor
                            class="election-manual-candidate__rich-text"
                            [defaultHeight]="200"
                            [modelValue]="campaignBrief()"
                            (modelValueChange)="campaignBrief.set($event ?? '')"
                        ></s25-ng-rich-text-editor>
                    </div>
                    <div class="form-group c-margin-bottom--single">
                        <label>Photo</label>
                        @if (candidatePhotoFileKey() == null) {
                            <p class="c-text--secondary">Select election, position, and contact to upload a photo.</p>
                        } @else {
                            @for (photoSlot of candidatePhotoSlotKeys(); track photoSlot) {
                                <s25-ng-filestore-image
                                    class="election-manual-candidate__photo"
                                    [value]="photoUrl()"
                                    (valueChange)="photoUrl.set($event)"
                                    [objectType]="-1"
                                    [objectId]="-1"
                                    [fileKey]="photoSlot"
                                    [circle]="true"
                                    [circleSizePx]="120"
                                />
                            }
                        }
                    </div>
                    <s25-ng-button [type]="'primary'" [onClick]="submit" [disabled]="!canSubmit()"
                        >Save Candidate</s25-ng-button
                    >
                </form>
            }
        </div>
    `,
    styles: `
        .election-manual-candidate__form .form-group {
            display: grid;
            grid-template-columns: minmax(10.5rem, 16rem) minmax(0, 1fr);
            column-gap: 1.25rem;
            row-gap: 0.65rem;
            align-items: start;
            max-width: 48rem;
        }
        .election-manual-candidate__form .form-group > label {
            grid-column: 1;
            grid-row: 1 / -1;
            margin: 0;
            padding-top: 0.35rem;
        }
        .election-manual-candidate__form .form-group > :not(label) {
            grid-column: 2;
            min-width: 0;
        }
        .election-manual-candidate__control s25-ng-dropdown-search-criteria {
            display: block;
            max-width: 40rem;
        }
        .election-manual-candidate__rich-text {
            display: block;
            width: 100%;
            max-width: 40rem;
        }
        .election-manual-candidate__photo {
            max-width: 40rem;
        }
    `,
})
export class ElectionManualCandidatePageComponent implements OnInit {
    readonly login = inject(LoginStore);
    private readonly electionAdminReturn = inject(ElectionAdminReturnStore);

    readonly selectedElectionDropDown = signal<DropDownItem<number> | null>(null);
    readonly electionId = computed(() => {
        const chosen = this.selectedElectionDropDown();
        const rawId = chosen?.itemId ?? chosen?.val;
        if (rawId == null) {
            return null;
        }
        const asNumber = Number(rawId);
        return Number.isFinite(asNumber) ? asNumber : null;
    });

    /**
     * One element, tracked by election id, so the position criteria dropdown remounts when the election
     * changes (search-criteria dropdown does not refetch when only customFilterValue changes).
     */
    readonly positionCriteriaRemountByElectionId = computed((): number[] => {
        const electionIdValue = this.electionId();
        return electionIdValue != null ? [electionIdValue] : [];
    });

    readonly selectedPositionDropDown = signal<DropDownItem<number> | null>(null);
    readonly positionId = computed(() => {
        const chosen = this.selectedPositionDropDown();
        const rawId = chosen?.itemId ?? chosen?.val;
        if (rawId == null) {
            return null;
        }
        const asNumber = Number(rawId);
        return Number.isFinite(asNumber) ? asNumber : null;
    });

    readonly contactItem = signal<DropDownItem | null>(null);
    readonly campaignBrief = signal("");
    readonly photoUrl = signal("");

    /**
     * Filestore uploads for manual candidate photos use object -1/-1 with a stable key per
     * election, position, and contact (same pattern as ingestion IMAGE fields).
     */
    readonly candidatePhotoFileKey = computed((): string | undefined => {
        const electionIdValue = this.electionId();
        const positionIdValue = this.positionId();
        const rawContactId = this.contactItem()?.itemId;
        if (electionIdValue == null || positionIdValue == null || rawContactId == null) {
            return undefined;
        }
        const contactId = typeof rawContactId === "number" ? rawContactId : Number(rawContactId);
        if (Number.isNaN(contactId)) {
            return undefined;
        }
        return `seEcand&${electionIdValue}&${positionIdValue}&${contactId}`;
    });

    readonly candidatePhotoSlotKeys = computed(() => {
        const key = this.candidatePhotoFileKey();
        return key != null ? [key] : [];
    });

    readonly backToList = () => {
        const electionIdValue = this.electionId();
        if (electionIdValue != null) {
            this.electionAdminReturn.scheduleReturnToElection(electionIdValue);
        }
        void StateService.go("seElectionAdmin", {} satisfies S25States.ParamsForName<"seElectionAdmin">, {});
    };

    ngOnInit(): void {
        const st = StateService.getState();
        const p = st?.params as { electionId?: string } | undefined;
        const eid = p?.electionId ? Number(p.electionId) : NaN;
        if (!Number.isNaN(eid) && eid > 0) {
            this.selectedElectionDropDown.set({
                itemId: eid,
                itemName: `Election ${eid}`,
            });
        }
    }

    onElectionDropDownChange(chosen: DropDownItem<number> | null | undefined) {
        this.selectedElectionDropDown.set(chosen ?? null);
        this.selectedPositionDropDown.set(null);
        this.photoUrl.set("");
    }

    onPositionDropDownChange(chosen: DropDownItem<number> | null | undefined) {
        this.selectedPositionDropDown.set(chosen ?? null);
        this.photoUrl.set("");
    }

    onContactItemChange(chosen: DropDownItem | null | undefined) {
        this.contactItem.set(chosen ?? null);
        this.photoUrl.set("");
    }

    canSubmit(): boolean {
        return this.electionId() != null && this.positionId() != null && !!this.contactItem()?.itemId;
    }

    submit = async () => {
        const eid = this.electionId();
        const pid = this.positionId();
        const cid = this.contactItem()?.itemId;
        if (eid == null || pid == null || cid == null) {
            return;
        }
        try {
            const contId = typeof cid === "number" ? cid : Number(cid);
            if (Number.isNaN(contId)) {
                throw new Error("Invalid contact");
            }
            await ElectionApi.upsertCandidate(eid, pid, {
                contId,
                campaignBrief: this.campaignBrief() || undefined,
                photoUrl: this.photoUrl() || undefined,
            });
            await AlertService.alert("Candidate saved.");
            this.campaignBrief.set("");
            this.photoUrl.set("");
            this.contactItem.set(null);
        } catch (e) {
            await AlertService.alert(S25Util.errorText(e));
        }
    };
}
